/**
 * Events API Service
 * Wraps GET /api/events and GET /api/feedback/:id for the admin /events view.
 * Step 1 of #327 — `list()` covers the table; `getFeedback()` will be wired up
 * by the FeedbackDetailPanel in step 2.
 */

import apiClient from './api';
import {
  ApiResponse,
  CreateIssueResponse,
  EventsListResponse,
  ErrorEventDetail,
  FeedbackEventDetail,
} from './types';

export interface ListEventsParams {
  type?: 'feedback' | 'error' | 'all';
  status?: 'new' | 'reviewed' | 'resolved' | 'wontfix' | 'all';
  source?: 'api' | 'errorBoundary' | 'all';
  before?: string;
  limit?: number;
}

export const eventsApi = {
  /** GET /api/events — admin-only list of feedback + error events. */
  async list(params: ListEventsParams = {}): Promise<EventsListResponse> {
    const response = await apiClient.get<ApiResponse<EventsListResponse>>('/events', { params });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch events');
    }
    return response.data.data;
  },

  /** GET /api/feedback/:id — detail payload for a single feedback entry. */
  async getFeedback(id: string): Promise<FeedbackEventDetail> {
    const response = await apiClient.get<ApiResponse<FeedbackEventDetail>>(`/feedback/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch feedback detail');
    }
    return response.data.data;
  },

  /** GET /api/errors/:id — detail payload for a single captured error. */
  async getError(id: string): Promise<ErrorEventDetail> {
    const response = await apiClient.get<ApiResponse<ErrorEventDetail>>(`/errors/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch error detail');
    }
    return response.data.data;
  },

  /**
   * POST /api/feedback/:id/issue — create (or return existing) GitHub issue
   * for a feedback entry. Idempotent: a second call returns the existing
   * issue with `alreadyExisted: true`.
   *
   * Wired up by `CreateIssueButton` inside `FeedbackDetailPanel`. The
   * server-side endpoint lands with #332; the FE here is the optimistic
   * stub against the planned contract.
   */
  async createFeedbackIssue(id: string): Promise<CreateIssueResponse> {
    const response = await apiClient.post<ApiResponse<CreateIssueResponse>>(
      `/feedback/${id}/issue`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create GitHub issue');
    }
    return response.data.data;
  },

  /**
   * POST /api/errors/:id/issue — create (or return existing) GitHub issue
   * for an error entry. Same idempotency contract as `createFeedbackIssue`.
   */
  async createErrorIssue(id: string): Promise<CreateIssueResponse> {
    const response = await apiClient.post<ApiResponse<CreateIssueResponse>>(
      `/errors/${id}/issue`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create GitHub issue');
    }
    return response.data.data;
  },
};
